import React, { useMemo, useRef, useState } from 'react';
import {
  useUsersAndGroupsContext,
  useGetSession,
  UsersAndGroupsContextProvider,
} from '@opengov/bnp-core';
import {
  getSortedOptions,
  getSubjectPublicationClaimsToDelete,
  getUpdatedPublicationPermissionsOnOptionsSelected,
  updatePublicationClaimsForSubject,
  updatePublicationClaimsSectionIdsForSubject,
} from '../../../stories/utils/subjectSelect';
import { ISubjectSelectOption } from '../../../types/subjectTypes';
import {
  IPublicationPrivilege,
  IsubjectClaimsMessages,
  ISubjectPublicationClaimsList,
} from '../../../types';
import { StoryGranularAccessDialog } from '../../../stories/components/StoryAccessGranularDialog/StoryAccessGranularDialog';
import { usePublicationFolderStructure } from '../../../stories/components/Publications/PublicationView/hooks/usePublicationFolderStructure';
import { SelectChangeEvent } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  useDeleteSubjectPublicationClaim,
  useSaveSubjectPublicationClaim,
  useSubjectPublicationClaims,
  subjectClaimsQueryKeys
} from '../../../stories/hooks/subjectClaims';
import { fetchPublicationClaims } from '../../../stories/api/subjectClaimsApi';
export interface IPublicationGranularAccessProps {
  closeModal(): void;
  entitySubdomain: string;
  entityId: number;
  entityUuid: string;
  publicationId: string;
  messages: IsubjectClaimsMessages;
}

export const PublicationGranularAccessView: React.FC<IPublicationGranularAccessProps> = (props: IPublicationGranularAccessProps) => {
  const { closeModal, publicationId, entityId, entityUuid, messages } = props;
  const queryClient = useQueryClient();
  const saveAccessMutation = useSaveSubjectPublicationClaim();
  const deleteAccessMutation = useDeleteSubjectPublicationClaim();
  const { data: fetchedPublicationClaims } = useSubjectPublicationClaims(
    publicationId ? { publicationId } : null
  );
  const [editedPublicationClaims, setEditedPublicationClaims] = useState<ISubjectPublicationClaimsList | null>(null);
  const subjectPublicationClaimsList = editedPublicationClaims ?? fetchedPublicationClaims ?? [];
  const { publicationFolderStructure: folderStructure } = usePublicationFolderStructure(
    publicationId && entityId > 0 ? { publicationId, entityId, sectionIds: 'All' } : null,
    messages.somethingWentWrongError
  );
  const [selectedSubjectPublicationClaimsList, setSelectedSubjectPublicationClaimsList] = useState<ISubjectPublicationClaimsList>([]);
  const [subjectIdsToDelete, setSubjectIdsToDelete] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputName = 'subjectSelect';

  const { activeUsers, groupWithMembers, usersMap, groupsMap } = useUsersAndGroupsContext();
  const { data: session } = useGetSession();

  const sortedOptionsList = useMemo(() => {
    return getSortedOptions({ session, users: activeUsers, groups: groupWithMembers, subjectPublicationClaimsList });
  }, [session, activeUsers, groupWithMembers, subjectPublicationClaimsList]);

  const onOptionsSelected = (options: ISubjectSelectOption[]) => {
    if (!options.length) {
      return;
    }
    const [updatedClaims, updatedSelected] = getUpdatedPublicationPermissionsOnOptionsSelected(subjectPublicationClaimsList, selectedSubjectPublicationClaimsList, options, publicationId);
    setEditedPublicationClaims(updatedClaims);
    setSelectedSubjectPublicationClaimsList(updatedSelected);
  };

  const handleChange = (subjectId: string) => (e: SelectChangeEvent) => {
    const privilege = e.target.value as IPublicationPrivilege;
  
    const { updatedClaims, updatedSelectedClaims } = updatePublicationClaimsForSubject(
      subjectId,
      privilege,
      subjectPublicationClaimsList,
      selectedSubjectPublicationClaimsList
    );
  
    setEditedPublicationClaims(updatedClaims);
    setSelectedSubjectPublicationClaimsList(updatedSelectedClaims);
  };

  const handleSectionIdsChange = (subjectId: string) => (e: SelectChangeEvent<string[]>) => {
    const sectionIds = e.target.value as string[];
  
    const { updatedClaims, updatedSelectedClaims } = updatePublicationClaimsSectionIdsForSubject(
      subjectId,
      sectionIds,
      subjectPublicationClaimsList,
      selectedSubjectPublicationClaimsList
    );

    setEditedPublicationClaims(updatedClaims);
    setSelectedSubjectPublicationClaimsList(updatedSelectedClaims);
  };

  const handleClick = (subjectId: string) => {
    const { updatedSubjectIdsToDelete, updatedClaims, updatedSelectedClaims } = getSubjectPublicationClaimsToDelete(subjectId, subjectPublicationClaimsList, selectedSubjectPublicationClaimsList, subjectIdsToDelete);
    setSubjectIdsToDelete(updatedSubjectIdsToDelete);
    setEditedPublicationClaims(updatedClaims);
    setSelectedSubjectPublicationClaimsList(updatedSelectedClaims);
  };

  const handleSave = async () => {
    try {
      let didMutate = false;
      if (selectedSubjectPublicationClaimsList.length > 0) {
        await saveAccessMutation.mutateAsync({
          subjectPublicationClaimsList: selectedSubjectPublicationClaimsList,
          publicationId
        });
        didMutate = true;
      }
      if (subjectIdsToDelete.length > 0) {
        await deleteAccessMutation.mutateAsync({
          subjectIds: subjectIdsToDelete,
          publicationId,
          entityId,
          entityUuid
        });
        didMutate = true;
      }
      if (didMutate) {
        await queryClient.fetchQuery({
          queryKey: subjectClaimsQueryKeys.publicationClaims({ publicationId }),
          queryFn: () => fetchPublicationClaims({ publicationId })
        });
      }
      closeModal();
    } catch {
      // Toast handled by mutation hooks.
    }
  };

  const cols: { title: string; alignment: 'left' | 'center'; width: string }[] = [
    { title: messages.name, alignment: 'left', width: '50%' },
    { title: messages.permissionLevel, alignment: 'left', width: '40%' },
    { title: '', alignment: 'center', width: '10%' }
  ];

  return (
    <StoryGranularAccessDialog
      isBulk={false}
      storyTitles={[]}
      inputRef={inputRef}
      inputName={inputName}
      messages={messages}
      sortedOptionsList={sortedOptionsList}
      cols={cols}
      subjectsToRender={subjectPublicationClaimsList}
      usersMap={usersMap}
      groupsMap={groupsMap}
      onClose={closeModal}
      onSave={handleSave}
      onOptionChange={(_, value) => onOptionsSelected(value)} 
      onPublicationPrivilegeChange={(e, subjectId) => handleChange(subjectId)(e)}
      onSectionIdsChange={(e, subjectId) => handleSectionIdsChange(subjectId)(e)}
      onRemoveClick={handleClick}
      isPublicationGranularAccess={true}
      folderStructure={folderStructure}
    />
  );
};

export const PublicationGranularAccess = (props: IPublicationGranularAccessProps) => {
  const { entitySubdomain } = props;
  return (
    <UsersAndGroupsContextProvider entitySubdomain={entitySubdomain}>
      <PublicationGranularAccessView {...props} />
    </UsersAndGroupsContextProvider>
  );
};
